import {
  addDays,
  addMonths,
  businessDaysBetween,
  daysInMonth,
  endOfIsoWeek,
  formatIsoDate,
  isIsoDate,
  isoWeekNumber,
  isoWeekday,
  monthKeyOf,
  monthRange,
  parseMonthKey,
  startOfIsoWeek,
  todayInAppZone,
  type EntryNoteListItemDto,
  type EntrySuggestionDto,
  type IsoDate,
  type MonthLockState,
  type TimeEntryDto,
} from '@tk/shared';
import {
  Ban,
  Bot,
  ChevronLeft,
  ChevronRight,
  Copy,
  CopyPlus,
  CornerDownRight,
  Lock,
  MessageSquareWarning,
  Pencil,
  Plus,
  SquareKanban,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import AsiakasKoodi from '../components/AsiakasKoodi';
import EhdotusVahvistus from '../components/EhdotusVahvistus';
import KirjausLomake, { type KirjausEsitaytto } from '../components/KirjausLomake';
import KirjausTiedot from '../components/KirjausTiedot';
import { useKaannos, type Kaannos, type Muotoilu } from '../lib/i18n';
import {
  useCopyDay,
  useCreateEntry,
  useDeleteEntry,
  useEntryNotes,
  useEntrySuggestions,
  useResolveEntryNote,
  useTaskLookup,
  useTimeEntries,
  useUpdateEntry,
} from '../lib/queries';
import { useSession } from '../lib/session';
import { savyTyyli } from '../lib/varit';

type Nakyma = 'viikko' | 'kuukausi';

/**
 * Kalenteri (suunnitelma 10).
 *
 * Näkymän tila on URL:ssa (`?nakyma=viikko&pvm=2026-08-17`), jotta näkymän
 * voi jakaa linkkinä ja sivun päivitys säilyttää paikan.
 *
 * Euromääriä ei näytetä kalenterissa kenellekään — ne kuuluvat
 * raportteihin. Laskutettavuus näkyy kieltomerkkinä, ei pelkkänä värinä,
 * jotta se erottuu myös mustavalkoisena ja värisokealle.
 *
 * Koneen ehdotukset (backlog 5) näkyvät samassa sarakkeessa kirjausten
 * kanssa mutta katkoviivalla ja robotin merkillä, eivätkä ne kartuta
 * päivän eivätkä viikon tuntisummaa. Ero on pidettävä näkyvänä: ehdotus ei
 * ole tunti, ennen kuin ihminen on sanonut kauanko.
 */
export default function KalenteriSivu() {
  const [params, setParams] = useSearchParams();
  const { settings, permissions } = useSession();
  const { t, m } = useKaannos();

  const today = todayInAppZone();
  const rawDate = params.get('pvm');
  const selectedDate: IsoDate = rawDate && isIsoDate(rawDate) ? rawDate : today;
  const nakyma: Nakyma = params.get('nakyma') === 'kuukausi' ? 'kuukausi' : 'viikko';

  const [lomake, setLomake] = useState<{
    paiva: IsoDate;
    entry: TimeEntryDto | null;
    esitaytto?: KirjausEsitaytto | null;
  } | null>(null);

  /**
   * Taulusta kirjaukseen (PROJEKTINHALLINTA 16): `?tehtava=<id>` hakee
   * tehtävän ja avaa lomakkeen tälle päivälle esitäytettynä. Parametri
   * poistetaan heti, jotta päivitys tai takaisin-nappi ei avaa lomaketta
   * toista kertaa.
   */
  const tehtavaParam = params.get('tehtava');
  const { data: tehtavaHaku, error: tehtavaVirhe } = useTaskLookup(tehtavaParam);
  useEffect(() => {
    if (!tehtavaParam) return;
    if (!tehtavaHaku && !tehtavaVirhe) return;
    if (tehtavaHaku) {
      setLomake({
        paiva: today,
        entry: null,
        esitaytto: {
          customerId: tehtavaHaku.key.customerId,
          projectId: tehtavaHaku.key.projectId,
          description: tehtavaHaku.task.title,
          task: { id: tehtavaHaku.task.id, title: tehtavaHaku.task.title },
        },
      });
    }
    const next = new URLSearchParams(params);
    next.delete('tehtava');
    setParams(next, { replace: true });
  }, [tehtavaParam, tehtavaHaku, tehtavaVirhe, params, setParams, today]);
  // Kortin klikkaus avaa tarkastelun, ei muokkausta: kirjaus katsotaan
  // useammin kuin sitä korjataan, eikä katsominen saa avata lomaketta,
  // josta pääsee vahingossa tallentamaan.
  const [tarkastelu, setTarkastelu] = useState<TimeEntryDto | null>(null);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  /**
   * Raahattavana oleva kirjaus. Tarvitaan, jotta päiväsarake tietää
   * raahauksen aikana, saako siihen pudottaa — lukittuun kuukauteen ei
   * saa, ja se on kerrottava jo raahatessa eikä vasta pudotuksen jälkeen
   * (suunnitelma 10).
   */
  const [raahattava, setRaahattava] = useState<TimeEntryDto | null>(null);
  /** Vahvistettavana oleva koneen ehdotus (backlog 5). */
  const [vahvistettava, setVahvistettava] = useState<EntrySuggestionDto | null>(null);

  const { year, month } = parseMonthKey(monthKeyOf(selectedDate));

  // Kuukausinäkymässä haetaan koko ruudukko, myös edellisen ja seuraavan
  // kuukauden reunapäivät — muuten viikon summa olisi vajaa juuri
  // kuukauden vaihteessa.
  const range = useMemo(() => {
    if (nakyma === 'viikko') {
      return { from: startOfIsoWeek(selectedDate), to: endOfIsoWeek(selectedDate) };
    }
    return {
      from: startOfIsoWeek(formatIsoDate(year, month, 1)),
      to: endOfIsoWeek(formatIsoDate(year, month, daysInMonth(year, month))),
    };
  }, [nakyma, selectedDate, year, month]);

  const { data, isPending, error } = useTimeEntries(range.from, range.to);
  const { data: ehdotukset } = useEntrySuggestions(range.from, range.to);
  const { data: huomautukset } = useEntryNotes(range.from, range.to);
  const poista = useDeleteEntry();
  const kopioi = useCopyDay();
  const siirra = useUpdateEntry();
  const luo = useCreateEntry();

  const entriesByDay = useMemo(() => {
    const map = new Map<IsoDate, TimeEntryDto[]>();
    for (const entry of data?.entries ?? []) {
      const list = map.get(entry.entryDate);
      if (list) list.push(entry);
      else map.set(entry.entryDate, [entry]);
    }
    return map;
  }, [data]);

  const suggestionsByDay = useMemo(() => {
    const map = new Map<IsoDate, EntrySuggestionDto[]>();
    for (const suggestion of ehdotukset ?? []) {
      const list = map.get(suggestion.entryDate);
      if (list) list.push(suggestion);
      else map.set(suggestion.entryDate, [suggestion]);
    }
    return map;
  }, [ehdotukset]);

  const lockedMonths = useMemo(() => {
    const set = new Set<string>();
    for (const lock of data?.monthLocks ?? []) {
      if (lock.locked) set.add(`${lock.year}-${String(lock.month).padStart(2, '0')}`);
    }
    return set;
  }, [data]);

  const isLocked = useCallback(
    (date: IsoDate) => lockedMonths.has(monthKeyOf(date)),
    [lockedMonths],
  );

  const navigoi = useCallback(
    (date: IsoDate, view: Nakyma = nakyma) => {
      const next = new URLSearchParams(params);
      next.set('nakyma', view);
      next.set('pvm', date);
      setParams(next, { replace: false });
    },
    [params, setParams, nakyma],
  );

  const siirry = useCallback(
    (askel: number) => {
      if (nakyma === 'viikko') {
        navigoi(addDays(startOfIsoWeek(selectedDate), askel * 7));
      } else {
        const target = parseMonthKey(addMonths(monthKeyOf(selectedDate), askel));
        navigoi(formatIsoDate(target.year, target.month, 1));
      }
    },
    [nakyma, navigoi, selectedDate],
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (lomake || tarkastelu || vahvistettava) return;

      const key = event.key.toLowerCase();
      if (event.key === 'ArrowLeft') siirry(-1);
      else if (event.key === 'ArrowRight') siirry(1);
      else if (key === 't') navigoi(today);
      else if (key === 'v') navigoi(selectedDate, 'viikko');
      else if (key === 'k') navigoi(selectedDate, 'kuukausi');
      else if (key === 'n' && permissions.canLogTime && !isLocked(selectedDate)) {
        setLomake({ paiva: selectedDate, entry: null });
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    siirry,
    navigoi,
    today,
    lomake,
    tarkastelu,
    vahvistettava,
    permissions.canLogTime,
    isLocked,
    selectedDate,
  ]);

  /**
   * Siirto toiseen päivään raahaamalla.
   *
   * Sama muutos kuin lomakkeen päiväkentästä — raahaus on hiiren oikotie,
   * ei oma toimintonsa. Näppäimistöltä sama tehdään lomakkeella (15).
   */
  async function siirraKirjaus(entry: TimeEntryDto, kohde: IsoDate) {
    setRaahattava(null);
    if (entry.entryDate === kohde) return;

    setIlmoitus(null);
    try {
      await siirra.mutateAsync({ id: entry.id, input: { entryDate: kohde } });
      setIlmoitus(t('kalenteri.siirretty', m.pvm(kohde)));
    } catch (virhe) {
      setIlmoitus(virhe instanceof Error ? virhe.message : t('kalenteri.siirtoEpaonnistui'));
    }
  }

  /**
   * Kirjauksen monistus (käyttäjien pyyntö).
   *
   * **Uusi kirjaus, ei viittaus.** Monistus menee tavallisen
   * luontipäätteen kautta, jolloin hinta ratkaistaan monistushetken
   * hinnastolla ja projektin aikataulun varoitukset syntyvät samalla
   * tavalla kuin käsin kirjatessa (5.3). Sama sääntö kuin edellisen
   * päivän kopioinnissa — kopio ei peri lähteen jäädytettyä hintaa.
   *
   * Laskutettavuus kopioidaan sellaisenaan: se on kirjauksen oma tieto,
   * jonka tekijä on voinut ohittaa (4.2). Palvelin ratkaisee sen silti
   * uudelleen, joten sisäinen asiakas pysyy laskuttamattomana.
   *
   * Huomautukset eivät kopioidu: ne koskevat sitä riviä, josta ne on
   * kirjoitettu.
   */
  async function monista(entry: TimeEntryDto, kohde: IsoDate) {
    setRaahattava(null);
    setIlmoitus(null);
    try {
      const tulos = await luo.mutateAsync({
        entryDate: kohde,
        customerId: entry.customer.id,
        taskTypeId: entry.taskType.id,
        projectId: entry.project?.id ?? null,
        minutes: entry.minutes,
        billable: entry.billable,
        description: entry.description,
      });
      setIlmoitus(
        [t('kalenteri.monistettu', m.pvm(kohde)), ...tulos.warnings.map((w) => w.message)].join(
          ' ',
        ),
      );
    } catch (virhe) {
      setIlmoitus(virhe instanceof Error ? virhe.message : t('kalenteri.monistusEpaonnistui'));
    }
  }

  function poistaKirjaus(entry: TimeEntryDto) {
    if (!window.confirm(t('kalenteri.poistetaanko'))) return;
    poista.mutate(entry.id);
    setTarkastelu(null);
  }

  async function kopioiEdellinen(paiva: IsoDate) {
    setIlmoitus(null);
    try {
      const tulos = await kopioi.mutateAsync({ from: addDays(paiva, -1), to: paiva });
      setIlmoitus(
        [t('kalenteri.kopioitu', tulos.created), ...tulos.warnings.map((w) => w.message)].join(' '),
      );
    } catch (virhe) {
      setIlmoitus(virhe instanceof Error ? virhe.message : t('kalenteri.kopiointiEpaonnistui'));
    }
  }

  const naytetytPaivat = data?.entries ?? [];
  const totalMinutes =
    nakyma === 'viikko'
      ? naytetytPaivat.reduce((sum, entry) => sum + entry.minutes, 0)
      : naytetytPaivat
          .filter((entry) => monthKeyOf(entry.entryDate) === monthKeyOf(selectedDate))
          .reduce((sum, entry) => sum + entry.minutes, 0);

  /**
   * Odotus arkipäivistä, myös viikkonäkymässä.
   *
   * Viikon odotus oli aiemmin kiinteä viikkotuntimäärä. Se on väärin
   * pyhäviikolla: juhannusviikko ei ole viisipäiväinen, eikä täyttöaste saa
   * väittää tekijän jääneen jälkeen päivästä, jota ei ollut. Sama funktio
   * kuin palvelimella (`businessDaysBetween`), jotta kalenterin luku ja
   * raportin luku eivät voi eriytyä (backlog 2.12).
   */
  const kuukausi = monthRange(year, month);
  const arkipaivia =
    nakyma === 'viikko'
      ? businessDaysBetween(range.from, range.to)
      : businessDaysBetween(kuukausi.from, kuukausi.to);
  const expectedMinutes = Math.round((Number(settings.defaultWeeklyHours) / 5) * arkipaivia * 60);
  const fillRate = expectedMinutes > 0 ? totalMinutes / expectedMinutes : 0;

  return (
    <div className="space-y-4">
      <div className="border-reuna bg-pinta-korotettu flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border px-4 py-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => siirry(-1)}
            aria-label={
              nakyma === 'viikko'
                ? t('kalenteri.edellinenViikko')
                : t('kalenteri.edellinenKuukausi')
            }
            className="border-reuna text-teksti-vaimea hover:text-teksti hover:border-reuna-vahva rounded-md border px-2 py-2"
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => siirry(1)}
            aria-label={
              nakyma === 'viikko' ? t('kalenteri.seuraavaViikko') : t('kalenteri.seuraavaKuukausi')
            }
            className="border-reuna text-teksti-vaimea hover:text-teksti hover:border-reuna-vahva rounded-md border px-2 py-2"
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </button>
        </div>

        <h1 className="text-teksti text-base font-semibold tracking-tight">
          {nakyma === 'viikko' ? (
            <>
              {t('kalenteri.viikkoNumero', isoWeekNumber(selectedDate))}
              {/* Pilkku eikä pelkkä marginaali: marginaali on tyhjää tilaa
                vain silmälle, ja ruudunlukijalle otsikko kuuluisi muodossa
                ”Viikko 3417.8.2026”. */}
              {', '}
              <span className="text-teksti-vaimea font-normal">
                {m.pvm(range.from)} – {m.pvm(range.to)}
              </span>
            </>
          ) : (
            m.kuukausiOtsikko(year, month)
          )}
        </h1>

        <button
          type="button"
          onClick={() => navigoi(today)}
          className="border-reuna text-teksti-vaimea hover:text-teksti hover:border-reuna-vahva rounded-md border px-3 py-1.5 text-sm"
        >
          {t('kalenteri.tanaan')}
        </button>

        <LukitusMerkki lukot={data?.monthLocks ?? []} />

        <div className="ml-auto flex items-center gap-3">
          <div className="text-right leading-tight">
            <div className="luvut text-teksti text-sm font-medium">
              {m.tunnit(totalMinutes)} / {m.tunnit(expectedMinutes)}
            </div>
            <div className="text-teksti-vaimea text-xs">
              {nakyma === 'viikko' ? t('kalenteri.viikonTunnit') : t('kalenteri.kuukaudenTunnit')}
            </div>
          </div>
          <div
            className="bg-reuna h-2 w-24 overflow-hidden rounded-full"
            role="img"
            aria-label={t('kalenteri.tayttoaste', Math.round(fillRate * 100))}
          >
            <div
              className="bg-brandi h-full rounded-full"
              style={{ width: `${Math.min(100, fillRate * 100)}%` }}
            />
          </div>
        </div>

        <div className="border-reuna flex overflow-hidden rounded-md border">
          {(['viikko', 'kuukausi'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => navigoi(selectedDate, value)}
              aria-pressed={nakyma === value}
              className={[
                'px-3 py-1.5 text-sm transition-colors',
                value === 'kuukausi' ? 'border-reuna border-l' : '',
                nakyma === value
                  ? 'bg-korostus-pehmea text-korostus font-medium'
                  : 'text-teksti-vaimea hover:text-teksti',
              ].join(' ')}
            >
              {value === 'viikko' ? t('kalenteri.viikko') : t('kalenteri.kuukausi')}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p
          role="alert"
          className="border-yli/40 bg-yli/10 text-teksti rounded-md border px-3 py-2 text-sm"
        >
          {error.message}
        </p>
      )}

      {ilmoitus && (
        <p
          role="status"
          className="border-reuna bg-pinta-korotettu text-teksti rounded-md border px-3 py-2 text-sm"
        >
          {ilmoitus}
        </p>
      )}

      {/* Huomautukset ennen ruudukkoa: ne ovat pyyntö korjata jokin jo
          kirjattu rivi, ja siksi ensimmäinen asia, joka kalenterissa
          kannattaa nähdä (backlog 3.5). */}
      <AvoimetHuomautukset
        huomautukset={huomautukset ?? []}
        onValitsePaiva={(paiva) => navigoi(paiva)}
        t={t}
        m={m}
      />

      {nakyma === 'viikko' ? (
        <div className="border-reuna overflow-x-auto rounded-lg border">
          <div className="grid min-w-[64rem] grid-cols-7">
            {Array.from({ length: 7 }, (_, index) => addDays(range.from, index)).map((day) => (
              <Paiva
                key={day}
                date={day}
                isToday={day === today}
                isSelected={day === selectedDate}
                locked={isLocked(day)}
                entries={entriesByDay.get(day) ?? []}
                suggestions={suggestionsByDay.get(day) ?? []}
                lataa={isPending}
                voiKirjata={permissions.canLogTime}
                t={t}
                m={m}
                voiKopioida={
                  permissions.canLogTime &&
                  (entriesByDay.get(day) ?? []).length === 0 &&
                  (entriesByDay.get(addDays(day, -1)) ?? []).length > 0
                }
                onLisaa={() => setLomake({ paiva: day, entry: null })}
                onKopioi={() => void kopioiEdellinen(day)}
                onAvaa={(entry) => setTarkastelu(entry)}
                onAvaaEhdotus={(suggestion) => setVahvistettava(suggestion)}
                onMuokkaa={(entry) => setLomake({ paiva: day, entry })}
                onPoista={(entry) => poistaKirjaus(entry)}
                onValitse={() => navigoi(day)}
                raahattava={raahattava}
                onRaahausAlkoi={setRaahattava}
                onRaahausPaattyi={() => setRaahattava(null)}
                onPudota={(entry) => void siirraKirjaus(entry, day)}
                onMonista={(entry) => void monista(entry, day)}
              />
            ))}
          </div>
        </div>
      ) : (
        <Kuukausiruudukko
          from={range.from}
          to={range.to}
          month={month}
          today={today}
          entriesByDay={entriesByDay}
          suggestionsByDay={suggestionsByDay}
          isLocked={isLocked}
          onValitsePaiva={(day) => navigoi(day, 'viikko')}
          t={t}
          m={m}
        />
      )}

      <p className="text-teksti-vaimea text-xs">
        {t('kalenteri.ohjeAlku')}{' '}
        <Ban className="inline size-3.5 align-text-bottom" aria-hidden="true" />{' '}
        {t('kalenteri.ohjeLoppu')}
        {nakyma === 'kuukausi' && t('kalenteri.ohjeKuukausi')}
      </p>

      {tarkastelu && (
        <KirjausTiedot
          entry={tarkastelu}
          locked={isLocked(tarkastelu.entryDate)}
          auki
          onSulje={() => setTarkastelu(null)}
          onMuokkaa={() => {
            setLomake({ paiva: tarkastelu.entryDate, entry: tarkastelu });
            setTarkastelu(null);
          }}
          onPoista={() => poistaKirjaus(tarkastelu)}
        />
      )}

      {lomake && (
        <KirjausLomake
          paiva={lomake.paiva}
          muokattava={lomake.entry}
          esitaytto={lomake.esitaytto}
          auki
          onSulje={() => setLomake(null)}
        />
      )}

      {vahvistettava && (
        <EhdotusVahvistus
          ehdotus={vahvistettava}
          auki
          onSulje={() => setVahvistettava(null)}
          onVahvistettu={setIlmoitus}
        />
      )}
    </div>
  );
}

/** Arkipäivät kuukaudessa — kuukausinäkymän odotusluku. */
function LukitusMerkki({ lukot }: { lukot: MonthLockState[] }) {
  const { t } = useKaannos();
  const lukitut = lukot.filter((lock) => lock.locked);
  if (lukitut.length === 0) return null;

  const nimet = lukitut
    .map((lock) => `${String(lock.month).padStart(2, '0')}/${lock.year}`)
    .join(', ');

  return (
    <span className="border-reuna bg-pinta text-teksti-vaimea flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs">
      <Lock className="size-3.5" aria-hidden="true" />
      {t('kalenteri.lukittu', nimet)}
    </span>
  );
}

/**
 * Kuukausiruudukko: katselu- ja tarkistusnäkymä (suunnitelma 10).
 * Siitä näkee heti, mitkä päivät ovat tyhjiä.
 */
function Kuukausiruudukko({
  from,
  to,
  month,
  today,
  entriesByDay,
  suggestionsByDay,
  isLocked,
  onValitsePaiva,
  t,
  m,
}: {
  from: IsoDate;
  to: IsoDate;
  month: number;
  today: IsoDate;
  entriesByDay: Map<IsoDate, TimeEntryDto[]>;
  suggestionsByDay: Map<IsoDate, EntrySuggestionDto[]>;
  isLocked: (date: IsoDate) => boolean;
  onValitsePaiva: (date: IsoDate) => void;
  t: Kaannos['t'];
  m: Muotoilu;
}) {
  const viikot: IsoDate[][] = [];
  for (let cursor = from; cursor <= to; cursor = addDays(cursor, 7)) {
    viikot.push(Array.from({ length: 7 }, (_, index) => addDays(cursor, index)));
  }

  return (
    <div className="border-reuna overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[52rem] table-fixed">
        <thead>
          <tr className="border-reuna bg-pinta-korotettu border-b">
            <th className="text-teksti-vaimea w-12 px-2 py-2 text-xs font-medium">
              {t('kalenteri.vk')}
            </th>
            {[1, 2, 3, 4, 5, 6, 7].map((weekday) => (
              <th
                key={weekday}
                className="text-teksti-vaimea px-2 py-2 text-xs font-medium uppercase"
              >
                {m.viikonpaivaLyhyt(weekday)}
              </th>
            ))}
            <th className="text-teksti-vaimea w-20 px-2 py-2 text-right text-xs font-medium">
              {t('kalenteri.yhteensa')}
            </th>
          </tr>
        </thead>
        <tbody>
          {viikot.map((viikko) => {
            const viikonMinuutit = viikko.reduce(
              (sum, day) =>
                sum + (entriesByDay.get(day) ?? []).reduce((acc, entry) => acc + entry.minutes, 0),
              0,
            );

            return (
              <tr key={viikko[0]} className="border-reuna border-b last:border-b-0">
                <td className="luvut text-teksti-vaimea border-reuna border-r px-2 py-1 text-center text-xs">
                  {isoWeekNumber(viikko[0]!)}
                </td>
                {viikko.map((day) => {
                  const entries = entriesByDay.get(day) ?? [];
                  const suggestions = suggestionsByDay.get(day) ?? [];
                  const minutes = entries.reduce((sum, entry) => sum + entry.minutes, 0);
                  const kuluvaKuukausi = Number(day.slice(5, 7)) === month;

                  return (
                    <td
                      key={day}
                      className={[
                        'border-reuna h-24 border-r p-0 align-top',
                        kuluvaKuukausi ? '' : 'opacity-40',
                        isLocked(day) ? 'bg-pinta' : '',
                      ].join(' ')}
                    >
                      <button
                        type="button"
                        onClick={() => onValitsePaiva(day)}
                        className="hover:bg-korostus-pehmea/40 flex h-full w-full flex-col items-stretch px-1.5 py-1 text-left"
                      >
                        <span className="flex items-baseline justify-between">
                          <span
                            className={[
                              'luvut text-xs',
                              day === today
                                ? 'bg-brandi rounded px-1 font-medium text-white'
                                : 'text-teksti-vaimea',
                            ].join(' ')}
                          >
                            {Number(day.slice(8, 10))}
                          </span>
                          {minutes > 0 && (
                            <span className="luvut text-teksti text-xs font-medium">
                              {m.tunnit(minutes, false)}
                            </span>
                          )}
                        </span>

                        <span className="mt-0.5 space-y-0.5 overflow-hidden">
                          {entries.slice(0, 3).map((entry) => (
                            <span
                              key={entry.id}
                              className="text-teksti-vaimea flex items-center gap-1 truncate text-[11px]"
                            >
                              {!entry.billable && (
                                <Ban
                                  className="size-3 shrink-0"
                                  aria-label={t('yleinen.eiLaskuteta')}
                                />
                              )}
                              {/* Piste eikä raitaa tai sirua: rivi on 11
                                pikseliä korkea eikä taustalle ole tilaa.
                                Kuukausinäkymästä katsotaan ensin, mitkä
                                päivät ovat tyhjiä — sävy on tässä
                                toissijainen vihje. */}
                              <span
                                className="asiakas-piste size-1.5 shrink-0 rounded-full"
                                style={savyTyyli(entry.customer.code, entry.customer.colorKey)}
                                aria-hidden="true"
                              />
                              {entry.customer.code} {m.tunnit(entry.minutes, false)}
                            </span>
                          ))}
                          {entries.length > 3 && (
                            <span className="text-teksti-vaimea block text-[11px]">
                              {t('kalenteri.muuta', entries.length - 3)}
                            </span>
                          )}
                          {/* Kuukausinäkymässä ehdotus näkyy lukumääränä eikä
                            rivinä: ruutu on 24 pikseliä korkea, ja ehdotus
                            vaatii perustelun lukemista, johon tarvitaan
                            viikkonäkymä tai vahvistusikkuna. */}
                          {suggestions.length > 0 && (
                            <span className="text-korostus flex items-center gap-1 text-[11px]">
                              <Bot className="size-3 shrink-0" aria-hidden="true" />
                              {t('kalenteri.ehdotuksia', suggestions.length)}
                            </span>
                          )}
                        </span>
                      </button>
                    </td>
                  );
                })}
                <td className="luvut text-teksti px-2 py-1 text-right text-xs">
                  {viikonMinuutit > 0 ? m.tunnit(viikonMinuutit) : '–'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Paiva({
  date,
  isToday,
  isSelected,
  locked,
  entries,
  suggestions,
  lataa,
  voiKirjata,
  voiKopioida,
  onLisaa,
  onKopioi,
  onAvaa,
  onAvaaEhdotus,
  onMuokkaa,
  onPoista,
  onValitse,
  raahattava,
  onRaahausAlkoi,
  onRaahausPaattyi,
  onPudota,
  onMonista,
  t,
  m,
}: {
  date: IsoDate;
  isToday: boolean;
  isSelected: boolean;
  locked: boolean;
  entries: TimeEntryDto[];
  /** Koneen avoimet ehdotukset tälle päivälle (backlog 5). */
  suggestions: EntrySuggestionDto[];
  lataa: boolean;
  voiKirjata: boolean;
  voiKopioida: boolean;
  onLisaa: () => void;
  onKopioi: () => void;
  onAvaa: (entry: TimeEntryDto) => void;
  onAvaaEhdotus: (suggestion: EntrySuggestionDto) => void;
  onMuokkaa: (entry: TimeEntryDto) => void;
  onPoista: (entry: TimeEntryDto) => void;
  onValitse: () => void;
  /** Parhaillaan raahattava kirjaus, tai `null`. */
  raahattava: TimeEntryDto | null;
  onRaahausAlkoi: (entry: TimeEntryDto) => void;
  onRaahausPaattyi: () => void;
  onPudota: (entry: TimeEntryDto) => void;
  /** Monistus tähän päivään — raahaus muokkausnäppäin pohjassa. */
  onMonista: (entry: TimeEntryDto) => void;
  t: Kaannos['t'];
  m: Muotoilu;
}) {
  const weekday = isoWeekday(date);
  const isWeekend = weekday >= 6;
  const dayOfMonth = Number(date.slice(8, 10));
  const month = Number(date.slice(5, 7));
  const minutes = entries.reduce((sum, entry) => sum + entry.minutes, 0);

  /**
   * Muokkausnäppäin raahauksen aikana tarkoittaa monistusta siirron
   * sijaan — sama sopimus kuin tiedostonhallinnassa. Kolme näppäintä,
   * koska sopimus on eri alustoilla eri: Ctrl (Windows, Linux), Alt tai
   * Cmd (macOS). Käyttäjä ei muista kumpi, ja väärä arvaus siirtäisi
   * kirjauksen kopioinnin sijaan.
   */
  const onMonistus = (event: { ctrlKey: boolean; metaKey: boolean; altKey: boolean }) =>
    event.ctrlKey || event.metaKey || event.altKey;

  // Pudotus sallitaan vain avoimeen päivään. Siirto vaatii lisäksi, että
  // lähde on toinen päivä; monistus ei — saman päivän monistus on juuri
  // se tapaus, johon toiminto useimmiten tarvitaan. Lukittu päivä ei kutsu
  // `preventDefault`ia, jolloin selain näyttää kieltokursorin jo raahauksen
  // aikana (suunnitelma 10) — se on parempi kuin torjuttu pudotus ja
  // virheilmoitus jälkikäteen.
  const voiPudottaa = (monistus: boolean) =>
    Boolean(raahattava) && !locked && (monistus || raahattava?.entryDate !== date);
  const [kohteena, setKohteena] = useState(false);

  return (
    <div
      onClick={onValitse}
      onDragOver={(event) => {
        const monistus = onMonistus(event);
        if (!voiPudottaa(monistus)) return;
        event.preventDefault();
        // Selain piirtää kursoriin plussan, kun `dropEffect` on `copy`.
        // Se on ainoa vihje siitä, kumpi tapahtuu, ja siksi se luetaan
        // joka `dragover`illa — näppäin voi nousta kesken raahauksen.
        event.dataTransfer.dropEffect = monistus ? 'copy' : 'move';
        setKohteena(true);
      }}
      onDragLeave={() => setKohteena(false)}
      onDrop={(event) => {
        setKohteena(false);
        const monistus = onMonistus(event);
        if (!voiPudottaa(monistus) || !raahattava) return;
        event.preventDefault();
        if (monistus) onMonista(raahattava);
        else onPudota(raahattava);
      }}
      className={[
        'border-reuna flex min-h-72 flex-col border-r last:border-r-0',
        isWeekend ? 'bg-pinta' : 'bg-pinta-korotettu',
        locked ? 'opacity-60' : '',
        isSelected ? 'ring-korostus/40 ring-2 ring-inset' : '',
        kohteena ? 'ring-korostus bg-korostus-pehmea/50 ring-2 ring-inset' : '',
      ].join(' ')}
    >
      {/* Tämä päivä: punainen viiva sarakkeen yläreunassa, kuten
        täyttöasteen palkki — sama punainen merkitsee "nyt". Viiva on
        varjo eikä reuna, jotta otsikkorivit pysyvät samankorkuisina. */}
      <div
        className={[
          'border-reuna flex items-baseline justify-between border-b px-3 py-2',
          isToday ? 'bg-korostus-pehmea/60 shadow-[inset_0_3px_0_var(--color-brandi)]' : '',
        ].join(' ')}
      >
        <span
          className={[
            'font-mono text-xs font-medium uppercase tracking-[0.08em]',
            isToday ? 'text-korostus' : 'text-teksti',
          ].join(' ')}
        >
          {m.viikonpaivaLyhyt(weekday)}{' '}
          <span className="luvut font-normal">
            {dayOfMonth}.{month}.
          </span>
        </span>
        <span className="luvut text-teksti-vaimea font-mono text-xs">
          {minutes > 0 ? m.tunnit(minutes) : '–'}
        </span>
      </div>

      <div className="flex-1 space-y-1.5 px-2 py-2">
        {lataa && <p className="text-teksti-vaimea px-1 text-xs">{t('yleinen.ladataan')}</p>}
        {!lataa && entries.length === 0 && suggestions.length === 0 && (
          <p className="text-teksti-vaimea px-1 text-center text-xs">
            {t('kalenteri.eiKirjauksia')}
          </p>
        )}
        {entries.map((entry) => (
          <Kirjaus
            key={entry.id}
            entry={entry}
            locked={locked}
            onAvaa={() => onAvaa(entry)}
            onMuokkaa={() => onMuokkaa(entry)}
            onPoista={() => onPoista(entry)}
            onMonista={() => onMonista(entry)}
            voiKirjata={voiKirjata}
            onRaahausAlkoi={() => onRaahausAlkoi(entry)}
            onRaahausPaattyi={onRaahausPaattyi}
            t={t}
            m={m}
          />
        ))}

        {/* Ehdotukset kirjausten jälkeen: kirjattu tunti on tosiasia,
          ehdotus vasta kysymys. Lukittu kuukausi näyttää ne silti — ne
          kertovat, mitä jäi kirjaamatta ennen lukitusta — mutta
          vahvistaminen torjutaan palvelimella (6.2). */}
        {suggestions.map((suggestion) => (
          <Ehdotus
            key={suggestion.id}
            ehdotus={suggestion}
            onAvaa={() => onAvaaEhdotus(suggestion)}
            t={t}
          />
        ))}
      </div>

      <div className="space-y-1 px-3 pb-3">
        {voiKopioida && !locked && (
          <button
            type="button"
            onClick={onKopioi}
            className="border-reuna text-teksti-vaimea hover:text-korostus hover:border-korostus flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed py-1 text-xs transition-colors"
          >
            <Copy className="size-3.5" aria-hidden="true" />
            {t('kalenteri.kopioiEdellinen')}
          </button>
        )}
        <button
          type="button"
          disabled={locked || !voiKirjata}
          onClick={onLisaa}
          title={locked ? t('kalenteri.kuukausiLukittu') : undefined}
          className="border-reuna text-teksti-vaimea hover:border-korostus hover:text-korostus flex w-full items-center justify-center rounded-md border border-dashed py-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={t('kalenteri.lisaaKirjaus', m.pvm(date))}
        >
          <Plus className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/**
 * Koneen ehdotus kalenterikortilla (backlog 5).
 *
 * Katkoviiva ja robotin merkki erottavat sen kirjauksesta jo kaukaa, ja
 * keston paikalla on kysymysmerkin sijaan viiva: ehdotuksella ei ole
 * kestoa, koska sitä ei ole vielä sanottu. Kortti on kokonaan
 * painike — sillä on vain yksi toiminto, avaaminen — toisin kuin
 * kirjauksen kortti, jonka sisällä on omat oikotiensä.
 */
function Ehdotus({
  ehdotus,
  onAvaa,
  t,
}: {
  ehdotus: EntrySuggestionDto;
  onAvaa: () => void;
  t: Kaannos['t'];
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onAvaa();
      }}
      aria-label={t('kalenteri.avaaEhdotus', ehdotus.customer.code)}
      // Siru mutta ei raitaa: raita tekisi ehdotuksesta liian kirjauksen
      // näköisen, ja ero on pidettävä näkyvänä. Katkoviiva ja robotin
      // merkki ovat ehdotuksen omat tunnukset, eikä asiakassävy saa
      // himmentää niitä.
      className="border-korostus/50 bg-korostus-pehmea/30 hover:border-korostus focus-visible:ring-korostus/60 block w-full rounded-md border border-dashed px-2 py-1.5 text-left text-xs transition-colors focus-visible:outline-none focus-visible:ring-2"
    >
      <span className="flex items-start justify-between gap-1">
        <span className="text-teksti flex items-center gap-1 font-medium">
          <Bot className="text-korostus size-3.5 shrink-0" aria-hidden="true" />
          <AsiakasKoodi code={ehdotus.customer.code} colorKey={ehdotus.customer.colorKey} />
        </span>
        <span className="luvut text-teksti-vaimea" aria-hidden="true">
          –
        </span>
      </span>
      <span className="text-teksti-vaimea block truncate">{ehdotus.taskType.name}</span>
      {ehdotus.project && (
        <span
          className="asiakas-teksti flex items-center gap-0.5 truncate"
          style={savyTyyli(ehdotus.customer.code, ehdotus.customer.colorKey)}
        >
          <CornerDownRight className="size-3 shrink-0" aria-hidden="true" />
          {ehdotus.project.name}
        </span>
      )}
      <span className="text-korostus mt-0.5 block">{t('kalenteri.vahvistaEhdotus')}</span>
    </button>
  );
}

/**
 * Yksi kirjaus kalenterikortilla.
 *
 * Kortti itse avaa tarkastelun; kynä ja roskakori ovat oikoteitä, jotka
 * pysäyttävät klikkauksen omaan tarkoitukseensa. Kortti on `div` eikä
 * `button`, koska napin sisällä ei saa olla nappeja — näppäimistökäyttö
 * hoidetaan `role`-, `tabIndex`- ja `keydown`-määrityksillä.
 */
function Kirjaus({
  entry,
  locked,
  voiKirjata,
  onAvaa,
  onMuokkaa,
  onPoista,
  onMonista,
  onRaahausAlkoi,
  onRaahausPaattyi,
  t,
  m,
}: {
  entry: TimeEntryDto;
  locked: boolean;
  /** Monistus luo uuden kirjauksen, joten se vaatii kirjausoikeuden. */
  voiKirjata: boolean;
  onAvaa: () => void;
  onMuokkaa: () => void;
  onPoista: () => void;
  onMonista: () => void;
  onRaahausAlkoi: () => void;
  onRaahausPaattyi: () => void;
  t: Kaannos['t'];
  m: Muotoilu;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={t('kalenteri.avaaKirjaus')}
      // Raahaus on hiiren oikotie kirjauksen siirtoon. Näppäimistöltä
      // sama tehdään muokkauslomakkeen päiväkentästä — raahaus ei saa
      // olla ainoa keino (15, WCAG 2.1 AA).
      draggable={!locked}
      onDragStart={(event) => {
        event.stopPropagation();
        // Teksti mukaan, jotta raahaus toimii myös silloin kun kohde on
        // sovelluksen ulkopuolella eikä selain saa muuta sisältöä.
        event.dataTransfer.setData('text/plain', entry.id);
        // `copyMove` eikä `move`: ilman sitä selain ei suostu näyttämään
        // kopiokursoria, vaikka pudotuskohde pyytäisi sitä.
        event.dataTransfer.effectAllowed = 'copyMove';
        onRaahausAlkoi();
      }}
      onDragEnd={onRaahausPaattyi}
      onClick={(event) => {
        event.stopPropagation();
        onAvaa();
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.stopPropagation();
        onAvaa();
      }}
      // Raita on koko kortin korkuinen ja siksi se, minkä silmä poimii
      // sarakkeesta ennen lukemista; siru on se, mihin katse pysähtyy kun
      // kortti luetaan. Molemmat ovat samaa sävyä (lib/varit.ts).
      style={savyTyyli(entry.customer.code, entry.customer.colorKey)}
      className={[
        'asiakas-raita border-reuna bg-pinta hover:border-korostus focus-visible:ring-korostus/60 group cursor-pointer rounded-md border border-l-[3px] px-2 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2',
        entry.billable ? '' : 'opacity-70',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-1">
        <span className="text-teksti flex items-center gap-1 font-medium">
          {!entry.billable && (
            <Ban className="size-3.5 shrink-0" aria-label={t('yleinen.eiLaskuteta')} />
          )}
          <AsiakasKoodi code={entry.customer.code} colorKey={entry.customer.colorKey} />
        </span>
        <span className="luvut text-teksti-vaimea">{m.tunnit(entry.minutes)}</span>
      </div>
      <div className="text-teksti-vaimea truncate">{entry.taskType.name}</div>
      {entry.project && (
        // Projektirivi asiakkaan sävyä: projekti ei saa omaa väriään, koska
        // kaksi saman asiakkaan kirjausta näyttäisi silloin kahdelta
        // asiakkaalta — ja juuri se ryhmittely on värien tarkoitus.
        <div className="asiakas-teksti flex items-center gap-0.5 truncate">
          <CornerDownRight className="size-3 shrink-0" aria-hidden="true" />
          {entry.project.name}
        </div>
      )}
      {entry.description && (
        <div className="text-teksti-vaimea truncate italic">{entry.description}</div>
      )}
      {entry.task && (
        // Linkki tehtävään, josta kirjaus kopioitiin (PROJEKTINHALLINTA 16).
        // Klikkaus ei avaa kirjausta: pysäytetään kortin oma käsittelijä.
        <Link
          to={`/taulut/tehtava/${entry.task.id}`}
          onClick={(event) => event.stopPropagation()}
          className="text-teksti-vaimea hover:text-korostus flex items-center gap-1 truncate"
          title={entry.task.title}
        >
          <SquareKanban className="size-3 shrink-0" aria-hidden="true" />
          <span className="truncate">{entry.task.title}</span>
        </Link>
      )}
      {entry.openNotes > 0 && (
        // Vain merkki, ei tekstiä: kortti on kapea, ja huomautukset luetaan
        // ja kuitataan kalenterin yläreunan listalta (backlog 3.5).
        <div className="text-varoitus mt-0.5 flex items-center gap-1">
          <MessageSquareWarning className="size-3.5 shrink-0" aria-hidden="true" />
          <span>{t('huomautus.merkki', entry.openNotes)}</span>
        </div>
      )}

      {!locked && (
        <div className="mt-1 flex gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          {/* Monistus on painikkeena eikä pelkkänä raahausoikotienä:
            raahaus ei toimi näppäimistöltä, eikä muokkausnäppäin ole
            löydettävissä mistään (15, WCAG 2.1 AA). Painike monistaa
            samaan päivään — toiseen päivään monistetaan raahaamalla tai
            monistamalla ensin ja siirtämällä sitten. */}
          {voiKirjata && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onMonista();
              }}
              title={t('kalenteri.monistaKirjaus')}
              aria-label={t('kalenteri.monistaKirjaus')}
              className="text-teksti-vaimea hover:text-korostus rounded p-1"
            >
              <CopyPlus className="size-3.5" aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onMuokkaa();
            }}
            title={t('yleinen.muokkaa')}
            aria-label={t('kalenteri.muokkaaKirjausta')}
            className="text-teksti-vaimea hover:text-korostus rounded p-1"
          >
            <Pencil className="size-3.5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onPoista();
            }}
            title={t('yleinen.poista')}
            aria-label={t('kalenteri.poistaKirjaus')}
            className="text-teksti-vaimea hover:text-yli rounded p-1"
          >
            <Trash2 className="size-3.5" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Avoimet huomautukset omista kirjauksista (backlog 3.5).
 *
 * Lista eikä pelkkä merkki korteilla: huomautus on toisen ihmisen pyyntö
 * korjata jokin, ja se on luettava kokonaan. Kortille mahtuu merkki, muttei
 * tekstiä eikä kuittausta.
 *
 * Kuittaus ei väitä, että kirjaus muuttui — sen näkee kirjauksesta itsestään
 * — vaan että huomautus on luettu ja hoidettu. Se ei myöskään hyväksy
 * mitään: hyväksyntäkierrosta ei ole (suunnitelma 16, kysymys A).
 */
function AvoimetHuomautukset({
  huomautukset,
  onValitsePaiva,
  t,
  m,
}: {
  huomautukset: EntryNoteListItemDto[];
  onValitsePaiva: (paiva: IsoDate) => void;
  t: Kaannos['t'];
  m: Muotoilu;
}) {
  const kuittaa = useResolveEntryNote();

  if (huomautukset.length === 0) return null;

  return (
    <section className="border-varoitus/50 bg-varoitus/10 rounded-lg border p-3">
      <h2 className="text-teksti flex items-center gap-1.5 text-sm font-medium">
        <MessageSquareWarning className="text-varoitus size-4 shrink-0" aria-hidden="true" />
        {t('huomautus.otsikko')}
      </h2>
      <p className="text-teksti-vaimea mt-0.5 text-xs">{t('huomautus.selite')}</p>

      <ul className="mt-2 space-y-2">
        {huomautukset.map((note) => (
          <li key={note.id} className="border-reuna bg-pinta rounded-md border px-3 py-2 text-xs">
            <div className="luvut text-teksti-vaimea flex flex-wrap items-baseline gap-x-2">
              <span>{m.pvm(note.entry.date)}</span>
              <AsiakasKoodi
                code={note.entry.customer.code}
                colorKey={note.entry.customer.colorKey}
              />
              <span className="text-teksti">{note.entry.taskType.name}</span>
              <span>{m.tunnit(note.entry.minutes)}</span>
            </div>
            <p className="text-teksti mt-1 whitespace-pre-wrap">{note.text}</p>
            <div className="text-teksti-vaimea mt-1 flex flex-wrap items-center gap-x-3">
              <span>
                {note.authorName} · {m.pvm(note.createdAt.slice(0, 10))}
              </span>
              <button
                type="button"
                onClick={() => onValitsePaiva(note.entry.date)}
                className="text-korostus hover:underline"
              >
                {t('huomautus.avaaPaiva')}
              </button>
              <button
                type="button"
                disabled={kuittaa.isPending}
                onClick={() => kuittaa.mutate({ id: note.id, resolved: true })}
                className="hover:text-korostus underline"
              >
                {t('huomautus.kuittaa')}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
